/**
 * Persists the super-admin analytics opt-out flag (see src/lib/analytics.ts)
 * once the signed-in user's profile resolves. Renders nothing — mounted in
 * App.tsx inside QueryClientProvider (useAdminAuth needs React Query), same
 * pattern as SentryUserSync.
 *
 * Flag lifecycle:
 * - super admin resolves        -> SET (localStorage + window + ga-disable)
 * - regular signed-in user      -> CLEAR (someone else owns this browser now;
 *                                  also replays the GA4 config skipped at boot)
 * - logged out                  -> KEEP, so a logged-out super admin browsing
 *                                  marketing pages stays untracked
 * - loading or query never ran  -> KEEP (never act on unresolved state; a
 *                                  paused offline query reports isLoading=false
 *                                  with no data, hence the roleResolved gate)
 *
 * public/gtag-init.js reads the persisted flag pre-React on the next load,
 * which is what keeps the very first pageview/GA hit suppressed. The initial
 * load on a fresh browser leaks a few beacons until the profile resolves —
 * accepted one-time residual.
 */
import { useEffect } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { applySuperAdminOptOut, clearSuperAdminOptOut } from "@/lib/analytics";

export default function SuperAdminAnalyticsSync() {
  const { userId, isSuperAdmin, isLoading, roleResolved } = useAdminAuth();

  useEffect(() => {
    if (isLoading) return;
    if (!userId) return;
    if (!roleResolved) return;
    if (isSuperAdmin) {
      applySuperAdminOptOut();
    } else {
      clearSuperAdminOptOut();
    }
  }, [userId, isSuperAdmin, isLoading, roleResolved]);

  return null;
}
